import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFirefox,
  FaSafari,
  FaEdge,
  FaDownload,
  FaRocket,
  FaShieldAlt,
  FaSync,
  FaUsers,
  FaChartLine,
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaPlay,
  FaChrome,
  FaClock,
  FaSearch,
  FaFileAlt,
  FaCog,
  FaHeart,
  FaMobile,
  FaDesktop,
  FaCloud,
  FaLock,
  FaBolt,
  FaInfinity,
  FaCrown
} from 'react-icons/fa';

export default function HRBrowserExtensions() {
  const [activeTab, setActiveTab] = useState('features');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const features = [
    {
      icon: FaBolt,
      title: "One-Click HR Actions",
      description: "Perform common HR tasks directly from your browser with single clicks",
      color: "#3B82F6"
    },
    {
      icon: FaSearch,
      title: "Smart Employee Search",
      description: "Instantly find employee information across all HR systems",
      color: "#8B5CF6"
    },
    {
      icon: FaFileAlt,
      title: "Quick Document Access",
      description: "Access HR documents, policies, and forms without leaving your current tab",
      color: "#06B6D4"
    },
    {
      icon: FaClock,
      title: "Time-Saving Shortcuts",
      description: "Keyboard shortcuts and quick actions for frequent HR operations",
      color: "#10B981"
    },
    {
      icon: FaUsers,
      title: "Team Management",
      description: "Manage team members, approvals, and workflows from any webpage",
      color: "#F59E0B"
    },
    {
      icon: FaChartLine,
      title: "Real-time Analytics",
      description: "View HR metrics and dashboards as you browse other applications",
      color: "#EF4444"
    }
  ];

  const browsers = [
    {
      name: "Google Chrome",
      icon: FaChrome,
      color: "#4285F4",
      users: "10,000+",
      rating: "4.8",
      version: "2.1.4"
    },
    {
      name: "Mozilla Firefox",
      icon: FaFirefox,
      color: "#FF7139",
      users: "5,200+",
      rating: "4.7",
      version: "2.1.3"
    },
    {
      name: "Microsoft Edge",
      icon: FaEdge,
      color: "#0078D7",
      users: "3,800+",
      rating: "4.6",
      version: "2.1.2"
    },
    {
      name: "Apple Safari",
      icon: FaSafari,
      color: "#000000",
      users: "2,500+",
      rating: "4.5",
      version: "2.1.1"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "HR Director",
      company: "TechInnovate",
      content: "This extension saved our team 15+ hours per week in administrative tasks.",
      avatar: "/images/avatar1.jpg"
    },
    {
      name: "Marcus Rodriguez",
      role: "Talent Manager",
      company: "GrowthCorp",
      content: "The quick employee search feature is a game-changer for our recruitment process.",
      avatar: "/images/avatar2.jpg"
    },
    {
      name: "Emily Watson",
      role: "HR Operations",
      company: "StartUpScale",
      content: "Finally, an extension that understands what HR professionals actually need!",
      avatar: "/images/avatar3.jpg"
    }
  ];

  return (
    <>
      <style>{`
        .hrExtensionsHero {
          background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 50%, #e8eeff 100%);
          min-height: 100vh;
          position: relative;
          overflow: hidden;
        }

        .hrExtensionsContainer {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 2;
        }

        /* Animated Background Elements */
        .hrExtensionsOrbs {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          pointer-events: none;
        }

        .hrExtensionsOrb {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.15;
          animation: float 6s ease-in-out infinite;
        }

        .hrExtensionsOrb:nth-child(1) {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #3B82F6, #8B5CF6);
          top: 10%;
          left: 5%;
          animation-delay: 0s;
        }

        .hrExtensionsOrb:nth-child(2) {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, #06B6D4, #3B82F6);
          top: 60%;
          right: 10%;
          animation-delay: 2s;
        }

        .hrExtensionsOrb:nth-child(3) {
          width: 250px;
          height: 250px;
          background: linear-gradient(135deg, #10B981, #06B6D4);
          bottom: 20%;
          left: 15%;
          animation-delay: 4s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -20px) scale(1.05); }
        }

        /* Hero Section */
        .hrExtensionsHeroContent {
          text-align: center;
          padding: 120px 0 80px;
        }

        .hrExtensionsHeroBadge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(59, 130, 246, 0.1);
          color: #3B82F6;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 30px;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .hrExtensionsHeroTitle {
          font-size: 4rem;
          font-weight: 800;
          background: linear-gradient(135deg, #1E40AF, #7C3AED);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 20px 0;
          line-height: 1.1;
        }

        .hrExtensionsHeroSubtitle {
          font-size: 1.5rem;
          color: #64748B;
          margin: 0 0 40px 0;
          line-height: 1.6;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .hrExtensionsHeroButtons {
          display: flex;
          gap: 20px;
          justify-content: center;
          margin-bottom: 60px;
        }

        .hrExtensionsButton {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 32px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .hrExtensionsButtonPrimary {
          background: linear-gradient(135deg, #3B82F6, #8B5CF6);
          color: white;
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
        }

        .hrExtensionsButtonPrimary:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.4);
        }

        .hrExtensionsButtonSecondary {
          background: white;
          color: #3B82F6;
          border: 2px solid #3B82F6;
        }

        .hrExtensionsButtonSecondary:hover {
          background: #3B82F6;
          color: white;
          transform: translateY(-3px);
        }

        /* Browser Showcase */
        .hrExtensionsBrowserShowcase {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 30px;
          margin: 80px 0;
        }

        .hrExtensionsBrowserCard {
          background: white;
          padding: 30px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .hrExtensionsBrowserCard:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
        }

        .hrExtensionsBrowserIcon {
          font-size: 48px;
          margin-bottom: 20px;
        }

        .hrExtensionsBrowserName {
          font-size: 20px;
          font-weight: 700;
          color: #1F2937;
          margin: 0 0 10px 0;
        }

        .hrExtensionsBrowserStats {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #E5E7EB;
        }

        .hrExtensionsBrowserStat {
          text-align: center;
        }

        .hrExtensionsBrowserStatValue {
          font-size: 16px;
          font-weight: 700;
          color: #1F2937;
        }

        .hrExtensionsBrowserStatLabel {
          font-size: 12px;
          color: #6B7280;
        }

        /* Features Grid */
        .hrExtensionsFeatures {
          padding: 100px 0;
        }

        .hrExtensionsSectionTitle {
          text-align: center;
          font-size: 3rem;
          font-weight: 800;
          color: #1F2937;
          margin: 0 0 20px 0;
        }

        .hrExtensionsSectionSubtitle {
          text-align: center;
          font-size: 1.25rem;
          color: #6B7280;
          margin: 0 0 60px 0;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .hrExtensionsFeaturesGrid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
        }

        .hrExtensionsFeatureCard {
          background: white;
          padding: 40px 30px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .hrExtensionsFeatureCard:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .hrExtensionsFeatureIcon {
          font-size: 48px;
          margin-bottom: 20px;
        }

        .hrExtensionsFeatureTitle {
          font-size: 20px;
          font-weight: 700;
          color: #1F2937;
          margin: 0 0 15px 0;
        }

        .hrExtensionsFeatureDescription {
          color: #6B7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Demo Section */
        .hrExtensionsDemo {
          padding: 100px 0;
          background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
          border-radius: 40px;
          margin: 100px 0;
        }

        .hrExtensionsDemoContent {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .hrExtensionsDemoText {
          padding-right: 40px;
        }

        .hrExtensionsDemoTitle {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1F2937;
          margin: 0 0 20px 0;
        }

        .hrExtensionsDemoDescription {
          font-size: 1.125rem;
          color: #6B7280;
          line-height: 1.7;
          margin: 0 0 30px 0;
        }

        .hrExtensionsDemoVisual {
          position: relative;
        }

        .hrExtensionsDemoImage {
          width: 100%;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .hrExtensionsDemoOverlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        /* Testimonials */
        .hrExtensionsTestimonials {
          padding: 100px 0;
        }

        .hrExtensionsTestimonialsGrid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
        }

        .hrExtensionsTestimonialCard {
          background: white;
          padding: 40px 30px;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .hrExtensionsTestimonialCard:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .hrExtensionsTestimonialContent {
          font-size: 1.125rem;
          color: #4B5563;
          line-height: 1.7;
          margin: 0 0 30px 0;
          font-style: italic;
        }

        .hrExtensionsTestimonialAuthor {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .hrExtensionsTestimonialAvatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3B82F6, #8B5CF6);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
        }

        .hrExtensionsTestimonialInfo {
          flex: 1;
        }

        .hrExtensionsTestimonialName {
          font-weight: 700;
          color: #1F2937;
          margin: 0 0 5px 0;
        }

        .hrExtensionsTestimonialRole {
          color: #6B7280;
          font-size: 14px;
          margin: 0;
        }

        /* CTA Section */
        .hrExtensionsCTA {
          background: linear-gradient(135deg, #3B82F6, #8B5CF6);
          padding: 100px 0;
          border-radius: 40px;
          text-align: center;
          color: white;
          margin: 100px 0;
          position: relative;
          overflow: hidden;
        }

        .hrExtensionsCTATitle {
          font-size: 3rem;
          font-weight: 800;
          margin: 0 0 20px 0;
        }

        .hrExtensionsCTADescription {
          font-size: 1.25rem;
          opacity: 0.9;
          margin: 0 0 40px 0;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .hrExtensionsCTAButtons {
          display: flex;
          gap: 20px;
          justify-content: center;
        }

        .hrExtensionsCTAButton {
          background: white;
          color: #3B82F6;
          border: none;
        }

        .hrExtensionsCTAButton:hover {
          background: #1E40AF;
          color: white;
        }

        /* Scroll to Top */
        .hrExtensionsScrollTop {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #3B82F6, #8B5CF6);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
        }

        .hrExtensionsScrollTop:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 30px rgba(59, 130, 246, 0.4);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hrExtensionsHeroTitle {
            font-size: 2.5rem;
          }

          .hrExtensionsHeroButtons {
            flex-direction: column;
            align-items: center;
          }

          .hrExtensionsDemoContent {
            grid-template-columns: 1fr;
          }

          .hrExtensionsDemoText {
            padding-right: 0;
          }

          .hrExtensionsCTAButtons {
            flex-direction: column;
            align-items: center;
          }

          .hrExtensionsButton {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }
        }
      `}</style>

      <div className="hrExtensionsHero">
        <div className="hrExtensionsOrbs">
          <div className="hrExtensionsOrb"></div>
          <div className="hrExtensionsOrb"></div>
          <div className="hrExtensionsOrb"></div>
        </div>

        <div className="hrExtensionsContainer">
          {/* Hero Section */}
          <div className="hrExtensionsHeroContent">
            <div className="hrExtensionsHeroBadge">
              <FaChrome />
              Browser Extension
            </div>
            <h1 className="hrExtensionsHeroTitle">
              HR Copilot Browser Extension
            </h1>
            <p className="hrExtensionsHeroSubtitle">
              Supercharge your HR workflow with our powerful browser extension. 
              Access HR tools, employee data, and automation features directly from your browser.
            </p>
            <div className="hrExtensionsHeroButtons">
              <button className="hrExtensionsButton hrExtensionsButtonPrimary">
                <FaDownload />
                Add to Chrome - Free
              </button>
              <button className="hrExtensionsButton hrExtensionsButtonSecondary">
                <FaPlay />
                Watch Demo
              </button>
            </div>

            {/* Browser Support */}
            <div className="hrExtensionsBrowserShowcase">
              {browsers.map((browser, index) => (
                <div key={index} className="hrExtensionsBrowserCard">
                  <browser.icon 
                    className="hrExtensionsBrowserIcon" 
                    style={{ color: browser.color }}
                  />
                  <h3 className="hrExtensionsBrowserName">{browser.name}</h3>
                  <div className="hrExtensionsBrowserStats">
                    <div className="hrExtensionsBrowserStat">
                      <div className="hrExtensionsBrowserStatValue">{browser.users}</div>
                      <div className="hrExtensionsBrowserStatLabel">Users</div>
                    </div>
                    <div className="hrExtensionsBrowserStat">
                      <div className="hrExtensionsBrowserStatValue">{browser.rating}</div>
                      <div className="hrExtensionsBrowserStatLabel">Rating</div>
                    </div>
                    <div className="hrExtensionsBrowserStat">
                      <div className="hrExtensionsBrowserStatValue">v{browser.version}</div>
                      <div className="hrExtensionsBrowserStatLabel">Version</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features Section */}
          <section className="hrExtensionsFeatures">
            <h2 className="hrExtensionsSectionTitle">Powerful Features</h2>
            <p className="hrExtensionsSectionSubtitle">
              Everything you need to streamline your HR processes, right in your browser
            </p>
            <div className="hrExtensionsFeaturesGrid">
              {features.map((feature, index) => (
                <div key={index} className="hrExtensionsFeatureCard">
                  <feature.icon 
                    className="hrExtensionsFeatureIcon" 
                    style={{ color: feature.color }}
                  />
                  <h3 className="hrExtensionsFeatureTitle">{feature.title}</h3>
                  <p className="hrExtensionsFeatureDescription">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Demo Section */}
          <section className="hrExtensionsDemo">
            <div className="hrExtensionsDemoContent">
              <div className="hrExtensionsDemoText">
                <h2 className="hrExtensionsDemoTitle">See It In Action</h2>
                <p className="hrExtensionsDemoDescription">
                  Our browser extension integrates seamlessly with your existing workflow. 
                  From quick employee lookups to instant document access, experience the future of HR technology.
                </p>
                <div className="hrExtensionsHeroButtons">
                  <button className="hrExtensionsButton hrExtensionsButtonPrimary">
                    <FaDownload />
                    Install Now
                  </button>
                  <button className="hrExtensionsButton hrExtensionsButtonSecondary">
                    <FaPlay />
                    View Tutorial
                  </button>
                </div>
              </div>
              <div className="hrExtensionsDemoVisual">
                <img 
                  src="/images/browser-extension-demo.png" 
                  alt="HR Copilot Browser Extension Demo"
                  className="hrExtensionsDemoImage"
                />
                <div className="hrExtensionsDemoOverlay">
                  <FaPlay size={24} color="#3B82F6" />
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="hrExtensionsTestimonials">
            <h2 className="hrExtensionsSectionTitle">Loved by HR Teams</h2>
            <p className="hrExtensionsSectionSubtitle">
              Join thousands of HR professionals who transformed their workflow
            </p>
            <div className="hrExtensionsTestimonialsGrid">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="hrExtensionsTestimonialCard">
                  <p className="hrExtensionsTestimonialContent">"{testimonial.content}"</p>
                  <div className="hrExtensionsTestimonialAuthor">
                    <div className="hrExtensionsTestimonialAvatar">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="hrExtensionsTestimonialInfo">
                      <h4 className="hrExtensionsTestimonialName">{testimonial.name}</h4>
                      <p className="hrExtensionsTestimonialRole">{testimonial.role} • {testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="hrExtensionsCTA">
            <h2 className="hrExtensionsCTATitle">Ready to Transform Your HR Workflow?</h2>
            <p className="hrExtensionsCTADescription">
              Join 20,000+ HR professionals who saved hours every week with our browser extension
            </p>
            <div className="hrExtensionsCTAButtons">
              <button className="hrExtensionsButton hrExtensionsCTAButton">
                <FaDownload />
                Add to Browser - Free
              </button>
              <button className="hrExtensionsButton hrExtensionsButtonSecondary">
                <FaPlay />
                Watch Demo
              </button>
            </div>
          </section>
        </div>
      </div>

      {isVisible && (
        <button 
          className="hrExtensionsScrollTop"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <FaArrowRight style={{ transform: 'rotate(-90deg)' }} />
        </button>
      )}
    </>
  );
}